import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that replaces the search button with an inline input
 * for development purposes.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} searchButton - The original search button
 * @property {HTMLInputElement} [inlineInput] - The inline search input (when active)
 * @extends {Component<Refs>}
 */
class InlineSearchComponent extends Component {
  requiredRefs = ['searchButton'];

  #isInlineMode = false;
  #originalButtonHTML = '';

  connectedCallback() {
    super.connectedCallback();

    // Check if inline mode is enabled in sessionStorage
    this.#isInlineMode = sessionStorage.getItem('dev-inline-search-mode') === 'true';
    
    // Store original button HTML
    this.#originalButtonHTML = this.refs.searchButton.outerHTML;

    // Initialize inline mode if enabled
    if (this.#isInlineMode) {
      this.#enableInlineMode();
    }

    // Initialize global dev commands
    this.#initDevCommands();
  }

  /**
   * Enable inline search mode - replace button with input
   */
  #enableInlineMode() {
    const button = this.refs.searchButton;
    
    // Create inline input element
    const inlineInput = document.createElement('input');
    inlineInput.type = 'search';
    inlineInput.placeholder = 'Buscar...';
    inlineInput.className = 'inline-search-input';
    inlineInput.setAttribute('ref', 'inlineInput');
    inlineInput.setAttribute('on:keydown', '/handleKeyDown');
    inlineInput.setAttribute('on:input', '/handleInput');

    // Replace button with input
    button.parentNode?.replaceChild(inlineInput, button);
    
    // Update refs
    this.refs.inlineInput = inlineInput;
    
    this.#isInlineMode = true;
    
    console.log('🔧 [DEV] Modo inline activado - input de búsqueda visible en el header');
  }

  /**
   * Disable inline search mode - restore original button
   */
  disableInlineMode() {
    const inlineInput = this.refs.inlineInput;
    
    if (inlineInput) {
      // Create new button from original HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.#originalButtonHTML;
      const newButton = tempDiv.firstElementChild;
      
      if (newButton) {
        // Replace input with button
        inlineInput.parentNode?.replaceChild(newButton, inlineInput);
        
        // Update refs
        this.refs.searchButton = /** @type {HTMLButtonElement} */ (newButton);
        delete this.refs.inlineInput;
      }
    }
    
    this.#isInlineMode = false;
    
    console.log('✅ [DEV] Modo inline desactivado - botón de búsqueda restaurado');
  }

  /**
   * Handle keydown events on the inline input
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#performSearch();
    }
    
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.refs.inlineInput) {
        this.refs.inlineInput.value = '';
        this.refs.inlineInput.blur();
      }
    }
  };

  /**
   * Handle input events on the inline input
   * @param {InputEvent} event - The input event
   */
  handleInput = debounce((event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const value = target.value.trim();
    
    // Just log for now - no predictive search
    if (value) {
      console.log(`🔍 [DEV] Búsqueda inline: "${value}"`);
    }
  }, 300);

  /**
   * Perform search when Enter is pressed
   */
  #performSearch() {
    const input = this.refs.inlineInput;
    if (!input) return;

    const searchTerm = input.value.trim();
    if (!searchTerm) return;

    // Navigate to search page
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', searchTerm);
    searchUrl.searchParams.set('type', 'product');
    
    window.location.href = searchUrl.toString();
  }

  /**
   * Initialize development commands for controlling inline search behavior
   */
  #initDevCommands() {
    // Only initialize once globally
    if (/** @type {any} */ (window).devInlineSearchCommands) return;

    /** @type {any} */ (window).devInlineSearchCommands = {
      /**
       * Enable inline search mode
       */
      enable: () => {
        sessionStorage.setItem('dev-inline-search-mode', 'true');
        // Update all existing instances
        document.querySelectorAll('inline-search-component').forEach(component => {
          if (component instanceof InlineSearchComponent) {
            if (!component.#isInlineMode) {
              component.#enableInlineMode();
            }
          }
        });
        console.log('🔧 [DEV] Modo de búsqueda inline activado.');
        console.log('   • El botón de búsqueda se reemplaza por un input visible');
        console.log('   • Puedes escribir directamente en el header');
        console.log('   • Presiona Enter para buscar, Escape para limpiar');
      },

      /**
       * Disable inline search mode
       */
      disable: () => {
        sessionStorage.removeItem('dev-inline-search-mode');
        // Update all existing instances
        document.querySelectorAll('inline-search-component').forEach(component => {
          if (component instanceof InlineSearchComponent) {
            if (component.#isInlineMode) {
              component.disableInlineMode();
            }
          }
        });
        console.log('✅ [DEV] Modo de búsqueda inline desactivado.');
        console.log('   • Botón de búsqueda original restaurado');
        console.log('   • Funcionalidad normal del modal restaurada');
      },

      /**
       * Check current inline search status
       */
      status: () => {
        const isEnabled = sessionStorage.getItem('dev-inline-search-mode') === 'true';
        console.log(`📊 [DEV] Estado de búsqueda inline: ${isEnabled ? 'ACTIVADO' : 'DESACTIVADO'}`);
        return isEnabled;
      }
    };

    // Show available commands on first load - COMMENTED OUT FOR PRODUCTION
    // console.log(`
    // 🔧 [DEV] Comandos de búsqueda inline disponibles:
    // - devInlineSearchCommands.enable() - Activa el modo de búsqueda inline
    // - devInlineSearchCommands.disable() - Desactiva el modo de búsqueda inline
    // - devInlineSearchCommands.status() - Muestra el estado actual
    // 
    // Estado actual: ${this.#isInlineMode ? 'ACTIVADO' : 'DESACTIVADO'}
    //     `);
  }
}

if (!customElements.get('inline-search-component')) {
  customElements.define('inline-search-component', InlineSearchComponent);
}
